<script setup>
import {computed, ref, defineProps, defineEmits} from "vue";
import ChevronDown from "@/components/Icons/ChevronDown.vue";
import { t } from "@/controllerLK/AvalLocalization";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  allCount: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const selectedItems = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const isDropdownDisabled = computed(() => props.items.length === 0);

const isShowDropdown = ref(false);
const toggleDropdown = () => {
  if (!isDropdownDisabled.value) {
    isShowDropdown.value = !isShowDropdown.value;
  }
};

const getItemName = (itemId) => {
  const item = props.items.find((i) => String(i.id) === String(itemId));
  return item?.name || "";
};
</script>

<template>
  <div class="story-dropdown">
    <SimpleDropdown
      :use-cross-icon="false"
      :max-height="451"
      :match-trigger-width="true"
      :is-visible="isShowDropdown"
      teleport="#app"
      @close="toggleDropdown"
    >
      <template #trigger>
        <div
          class="story-dropdown__trigger"
          :class="{'story-dropdown__trigger--disabled': isDropdownDisabled}"
          @click="toggleDropdown"
        >
          <div class="story-dropdown__info">
            <span
              class="story-dropdown__label"
              :class="{'story-dropdown__label--active': isShowDropdown}"
            >
              {{ label }}
            </span>
            <span class="story-dropdown__value">
              <template v-if="selectedItems.length === 0">
                {{ t("news_page_story.dropdown.not_chosen") }}
              </template>
              <template v-else-if="selectedItems.length === 1">
                {{ getItemName(selectedItems[0]) }}
              </template>
              <template v-else-if="selectedItems.length === allCount">
                {{ t("news_page_story.dropdown.all_chosen") }}
              </template>
              <template v-else>
                ({{ selectedItems.length }})
              </template>
            </span>
          </div>
          <ChevronDown
            :class="{'story-dropdown__icon--open': isShowDropdown}"
            class="story-dropdown__icon"
          />
        </div>
      </template>
      <template #dropdown-item>
        <AvalListItem
          v-for="item in items"
          :key="item.id"
          v-model="selectedItems"
          :use-checkbox="true"
          :item="item"
        >
          {{ item.name }}
        </AvalListItem>
      </template>
    </SimpleDropdown>
  </div>
</template>

<style scoped lang="scss">
.story-dropdown {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 8px;
    padding: 0 12px;
    height: 42px;
    background-color: var(--background-01);
    cursor: pointer;
    user-select: none;

    &--disabled {
      cursor: not-allowed;

      .story-dropdown__label,
      .story-dropdown__value,
      .story-dropdown__icon {
        color: var(--text-text-disabled);
      }
    }
  }

  &__info {
    display: flex;
    gap: 10px;
    min-width: 0;
  }

  &__label {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--text-text-main);

    &--active {
      color: var(--text-text-on);
    }
  }

  &__value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--text-text-pur);
  }

  &__icon {
    flex-shrink: 0;
    color: var(--icon-icon-secondary);
    transition: transform 0.2s ease;

    &--open {
      color: var(--icon-icon-on);
      transform: rotate(180deg);
    }
  }
}
</style>